import { useState } from "react";
import { Badge, Box, Collapse, Group, Stack, Text, Textarea, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { composeMessage, setAppInstructions, useAppInstructions } from "../lib/clientSettings";

const INSTRUCTION_EXAMPLES = ["Our users follow a halal diet; never suggest pork.", "Assume restaurant portions unless told otherwise.", "Name foods in British English (crisps, courgette)."];

/**
 * The optional `message` an AI route accepts: a note from the user for this call, plus standing
 * instructions the app adds to every call to customize behaviour.
 */
export function AiContextFields({ note, onNoteChange, placeholder }: { note: string; onNoteChange: (v: string) => void; placeholder?: string }) {
  const instructions = useAppInstructions();
  const [open, setOpen] = useState(Boolean(instructions));
  const message = composeMessage(note, instructions);

  return (
    <Stack gap={6}>
      <Textarea
        label="Note for this call (optional)"
        description="Anything your user wants to add, sent to the AI as extra context."
        placeholder={placeholder ?? "e.g. homemade, large portion, no cheese"}
        autosize
        minRows={1}
        maxRows={4}
        value={note}
        onChange={(e) => onNoteChange(e.currentTarget.value)}
      />
      <UnstyledButton onClick={() => setOpen((o) => !o)}>
        <Group gap={6}>
          {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <Text fz="sm" fw={600}>
            Your app's instructions
          </Text>
          {instructions.trim() && (
            <Badge size="xs" color="lime" c="ink.8">
              On
            </Badge>
          )}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Stack gap={6} pl={20}>
          <Text fz="xs" c="dimmed">
            Standing instructions your app adds to every call to customize results for your product. Saved in this browser and used for photo and text logging here, including the guided tours.
          </Text>
          <Textarea autosize minRows={2} maxRows={6} placeholder={INSTRUCTION_EXAMPLES[0]} value={instructions} onChange={(e) => setAppInstructions(e.currentTarget.value)} />
          <Group gap={6}>
            {INSTRUCTION_EXAMPLES.map((ex) => (
              <Badge key={ex} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => setAppInstructions(ex)}>
                {ex}
              </Badge>
            ))}
          </Group>
        </Stack>
      </Collapse>
      {message && (
        <Box>
          <Text fz={11} c="dimmed">
            Sent as the <code>message</code> field:
          </Text>
          <Text fz="xs" ff="monospace" style={{ whiteSpace: "pre-wrap", background: "var(--pd-cream)", borderRadius: 6, padding: "6px 8px" }}>
            {message}
          </Text>
        </Box>
      )}
    </Stack>
  );
}
